import React, { useState, useRef, useEffect, useContext } from "react";
import Header from "../../components/common/Header-w";
import Button from "../../components/ui/Button2";
import EditText from "../../components/ui/EditText";
import TextArea from "../../components/ui/TextArea";
import CountUp from "../../components/ui/CountUp";
import JobSlider from "../JobTitleSlider";
import "../../Website.css";
import { useLocation } from "react-router-dom";
import CareerFooter from "../AboutUs/CareerFooter";
import { Employee_enquiry } from "../../../Services/Website/WebsiteApi";
import { LoaderContext } from "../../../Context/ContextProvider";

const EmployerPage = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    companyName: "",
    phoneNumber: "",
    email: "",
    teamSize: "",
    requirement: "",
    message: "",
  });
  const [statusMessage, setStatusMessage] = useState({ show: false, type: '', message: '' });

  const featureTrackRef = useRef(null);
  const featureScrollContainerRef = useRef(null);
  const statsSectionRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const location = useLocation();

  const [errors, setErrors] = useState({});

  const { setLoader } = useContext(LoaderContext);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const yOffset = -50; // Adjust to your header height
          const y =
            el.getBoundingClientRect().top + window.pageYOffset + yOffset;
          el.scrollIntoView({ top: y, behavior: "smooth" });
        }
      }, 100); // Delay ensures element is rendered
    }
  }, [location]);

  useEffect(() => {
    const track = featureTrackRef.current;
    const scroller = featureScrollContainerRef.current;

    if (!track || !scroller) return;

    const isDesktop = () => window.innerWidth >= 1024; // Tailwind's 'lg' breakpoint

    const setTrackHeight = () => {
      if (!isDesktop()) {
        track.style.height = "auto"; // On mobile, let height be natural
        return;
      }
      // The scrollable width is the total width of the cards minus the visible container width.
      const scrollableWidth = scroller.scrollWidth - scroller.clientWidth;
      // To make the scroll feel natural, we can set the vertical scroll distance
      // required to traverse the cards equal to the horizontal distance we need to scroll.
      // The total height of the track will be this scroll distance plus one viewport height (for the sticky part).
      const newHeight = scrollableWidth + window.innerHeight;
      track.style.height = `${newHeight}px`;
    };

    const resizeObserver = new ResizeObserver(setTrackHeight);
    resizeObserver.observe(scroller);

    setTrackHeight();
    window.addEventListener("resize", setTrackHeight);

    const handleScroll = () => {
      // Only run the scroll-hijacking logic on desktop
      if (!isDesktop()) {
        return;
      }

      const trackRect = track.getBoundingClientRect();

      // Only animate when the track is in the "sticky" viewport area
      if (trackRect.top > 0) {
        scroller.scrollLeft = 0;
        return;
      }
      if (trackRect.bottom < window.innerHeight) {
        scroller.scrollLeft = scroller.scrollWidth - scroller.clientWidth;
        return;
      }

      const scrollableWidth = scroller.scrollWidth - scroller.clientWidth;
      const trackScrollableHeight = track.offsetHeight - window.innerHeight;

      if (trackScrollableHeight <= 0) return;

      // Calculate the scroll progress within the track (0 to 1)
      const progress = -trackRect.top / trackScrollableHeight;
      const clampedProgress = Math.max(0, Math.min(1, progress));

      scroller.scrollLeft = clampedProgress * scrollableWidth;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", setTrackHeight);
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
          observer.unobserve(entry.target); // Animate only once
        }
      },
      {
        threshold: 0.1, // Trigger when 10% of the section is visible
      }
    );

    const currentRef = statsSectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10); // only digits, max 10

    const part1 = digits.slice(0, 3);
    const part2 = digits.slice(3, 6);
    const part3 = digits.slice(6, 10);

    if (digits.length === 0) return "";
    if (digits.length <= 3) return `(${part1}`;
    if (digits.length <= 6) return `(${part1}) ${part2}`;
    return `(${part1}) ${part2}-${part3}`;
  };


  const handleInputChange = (field) => (e) => {
    let input = e.target.value;
    // Only process phone number field
    if (field === "phoneNumber") {
      const formatted = formatPhoneNumber(input);
      setFormData((prev) => ({ ...prev, [field]: formatted }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: input }));
    }
  };
  const handleKeyDown = (e) => {
    const allowed = ["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"];
    if (!/[0-9]/.test(e.key) && !allowed.includes(e.key)) {
      e.preventDefault(); // Block non-digit keys
    }
  };

  const successStats = [
    {
      value: "100%",
      label: "Free to Use",
      bgColor: "tw-bg-orange",
      icon: "/images/free.png",
    },
    {
      value: "40%",
      label: "better new hire retention",
      bgColor: "tw-bg-primary-green",
      icon: "/images/handshake.png",
    },
    {
      value: "3x",
      label: "candidate-job match accuracy",
      bgColor: "tw-bg-primary-blue-2",
      icon: "/images/accuracy.png",
    },
    {
      value: "85%",
      label: "faster hiring process",
      bgColor: "tw-bg-primary-blue-3",
      icon: "/images/fast.png",
    },
  ];

  const perfectForCards = [
    { title: "Startups", image: "/images/img_shutterstock_2317887043.png" },
    {
      title: "Growing Companies",
      image: "/images/img_shutterstock_2353012835.png",
    },
    { title: "Enterprise", image: "/images/img_shutterstock_1315451216.png" },
    { title: "Remote Teams", image: "/images/img_shutterstock_2534831851.png" },
  ];

  const quickStartOptions = [
    {
      title: "Post Your First Job",
      subtitle: "Live in 5 minutes",
      image: "/images/img_frame_2018777026.png",
    },
    {
      title: "Schedule Demo",
      subtitle: "See the platform in action",
      image: "/images/img_frame_2018777026_254x500.png",
    },
    {
      title: "Talk to Our Team",
      subtitle: "Live in 5 minutes",
      image: "/images/img_frame_2018777026_1.png",
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.fullName.trim())
      newErrors.fullName = "Full Name is required";
    if (!formData.companyName.trim())
      newErrors.companyName = "Company name is required";
    if (!formData.phoneNumber.trim())
      newErrors.phoneNumber = "Phone number is required";
    if (!formData.email.trim())
      newErrors.email = "Email is required";
    if (!formData.teamSize.trim())
      newErrors.teamSize = "Please select team size";
    if (!formData.requirement.trim())
      newErrors.requirement = "Please select requirement";
    if (!formData.message.trim())
      newErrors.message = "Message is required";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setStatusMessage({
        show: true,
        type: 'Error',
        text: "Please fill out all required fields."
      });
      return;
    }

    const apiParam = {
      full_name: formData.fullName,
      company_name: formData.companyName,
      phone: formData.phoneNumber,
      work_email: formData.email,
      team_size: formData.teamSize,
      requirement: formData.requirement,
      message: formData.message,
    };

    setLoader(true);

    try {
      const data = await Employee_enquiry(apiParam);
      if (data?.code === "ERR_BAD_REQUEST") {
        throw new Error(data.response.data.error || 'Request failed');
      }

      if (data.message) {
        setStatusMessage({
          show: true,
          type: 'success', text: data.message
        });
        setFormData({
          fullName: "",
          companyName: "",
          phoneNumber: "",
          email: "",
          teamSize: "",
          requirement: "",
          message: "",
        });
        setErrors({});
      } else {
        setStatusMessage({
          show: true,
          type: 'Error',
          text: "Something went wrong."
        });
      }
    } catch (error) {
      console.error('Error:', error);
      setStatusMessage({
        show: true,
        type: 'error',
        text:
          error?.response?.data?.error ||
          error?.message ||
          'Something went wrong. Please try again.',
      });
    } finally {
      setLoader(false);
    }
  };

  return (
    <div style={{ marginTop: "65px" }} className="theme-career-partners tw-bg-gray-2 min-h-screen ">
      <Header />

      {/* Hero Section */}
      <section
        className="tw-relative tw-bg-cover tw-bg-center tw-bg-no-repeat tw-h-screen tw-flex tw-items-center tw-mt-16"
        style={{ backgroundImage: "url('/images/Bg-EmployerHome.jpeg')" }}
      >
        <div className="tw-w-full tw-max-w-[1682px] tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-14">
          <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-text-center">
            <div className="tw-relative">
              <h1 className="tw-text-4xl sm:tw-text-5xl md:tw-text-6xl lg:tw-text-6xl xl:tw-text-7xl tw-font-semibold tw-leading-tight tw-mb-4">
                <span className="tw-text-blue-light">
                  Find Your Perfect Hire in 30 Days
                </span>
              </h1>


              <p className="tw-text-white tw-text-xl sm:tw-text-2xl md:tw-text-3xl lg:tw-text-4xl tw-font-semibold tw-mb-12 tw-max-w-2xl tw-mx-auto">
                AI-powered talent matching that works.
              </p>

              <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-justify-center tw-items-center">
                <a href="#ContactUs">
                  <button
                    onClick={() => { }}
                    className="tw-border-none tw-inline-block tw-bg-gradient-to-r tw-from-[#0843FE] tw-to-[#4995FB] tw-text-white tw-font-medium tw-py-4 tw-px-8 tw-rounded-md focus:tw-ring-blue-500 tw-text-lg tw-transition tw-duration-300 tw-ease-in-out"
                  >
                    Schedule Demo
                  </button>
                </a>
                <a href="#ContactUs">
                  <button
                    className="tw-text-[16px] tw-bg-white tw-text-black tw-border tw-border-white tw-font-semibold tw-py-4 tw-px-8 tw-rounded-lg tw-transition tw-duration-300 hover:tw-bg-gray-300 hover:tw-text-black tw-text-lg"
                    onClick={() => { }}
                  >
                    Start Hiring Now
                  </button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        ref={featureTrackRef}
        id="TopCompany"
        className="tw-relative tw-border-t tw-border-gray-300 tw-py-16 lg:tw-py-0"
      >
        <div className="lg:tw-sticky tw-top-0 lg:tw-h-screen tw-overflow-hidden">
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-h-full tw-items-start lg:tw-items-center">
            {/* Left Column: Static Watermark and Title */}
            <div className="tw-relative lg:tw-flex tw-flex-col tw-items-start tw-justify-center lg:tw-col-span-1 tw-h-full">
              <div className="tw-w-full tw-z-50">
                <img
                  src="/images/img_image_43.png"
                  alt="Watermark"
                  className="tw-w-96 tw-h-[30rem] sm:tw-w-[24rem] sm:tw-h-[47rem] tw-left-0  tw-object-fit tw-opacity-100"
                />
                <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center sm:tw-left-0 md:tw-left-24">
                  <h2
                    className="tw-text-4xl sm:tw-text-5xl md:tw-text-6xl tw-font-semibold tw-bg-clip-text tw-text-transparent"
                    style={{
                      backgroundImage:
                        "linear-gradient(to bottom, var(--text-blue-1), var(--text-dark-blue-1))",
                    }}
                  >
                    <span className="tw-block">Why Top</span>
                    <span className="tw-block">Companies</span>
                    <span className="tw-block tw-whitespace-nowrap">
                      Choose SeekJobs
                    </span>
                  </h2>
                </div>
              </div>
            </div>

            {/* Right Column: Horizontally Scrolling Cards */}
            <div
              ref={featureScrollContainerRef}
              className="tw-overflow-x-auto lg:tw-col-span-2 xl:tw-col-span-2 tw-no-scrollbar"
              style={{
                maskImage:
                  "linear-gradient(to right, transparent, black 250px)",
              }}
            >
              {/* Mobile Title (hidden on large screens) */}
              {/* <div className="lg:tw-hidden md:tw-hidden tw-text-center tw-px-4 tw-mb-8">
                <img
                  src="/images/img_image_43.png"
                  alt="Watermark"
                  className="lg:tw-hidden md:tw-hidden tw-w-24 tw-h-24 tw-left-0 tw-pt-36 tw-object-fit tw-opacity-100"
                />
                <h2 className="tw-text-4xl sm:tw-text-5xl tw-font-semibold tw-bg-gradient-to-b from-blue-1 to-dark-blue-1 tw-bg-clip-text tw-text-transparent">
                  Why Top Companies Choose SeekJobs
                </h2>
              </div> */}

              <div
                className="tw-flex tw-gap-6 tw-pb-4 tw-min-w-max"
                style={{ scrollSnapType: "x mandatory" }}
              >
                <div className="tw-bg-transparent tw-rounded-xl tw-p-6 tw-w-[150px] tw-flex-shrink-0"></div>
                <div className="tw-bg-orange tw-rounded-xl tw-px-6 tw-py-4 tw-w-[300px] tw-flex-shrink-0 tw-flex tw-flex-col tw-justify-between tw-h-[450px]">
                  <h3 className="tw-text-blue-2 tw-text-4xl sm:tw-text-5xl tw-font-semibold tw-mb-4">
                    Smart AI Matching
                  </h3>
                  <p className="tw-text-blue-2 tw-text-xl sm:tw-text-3xl lg:tw-text-2xl tw-font-semibold">
                    Pre-vetted, job-ready candidates delivered to your inbox. No
                    resume spam, just quality talent.
                  </p>
                </div>
                <div className="tw-bg-yellow tw-rounded-xl tw-px-6 tw-py-4 tw-w-[300px] tw-flex-shrink-0 tw-flex tw-flex-col tw-justify-between tw-h-[450px]">
                  <h3 className="tw-text-blue-2 tw-text-4xl sm:tw-text-5xl tw-font-semibold tw-mb-4">
                    Free Job Promotion
                  </h3>
                  <p className="tw-text-blue-2 tw-text-xl sm:tw-text-3xl lg:tw-text-2xl tw-font-semibold">
                    Your roles blast across platforms, email lists, and
                    university portals. Maximum visibility, zero cost.
                  </p>
                </div>
                <div className="tw-bg-primary-green tw-rounded-xl tw-px-6 tw-py-4 tw-w-[300px] tw-flex-shrink-0 tw-flex tw-flex-col tw-justify-between tw-h-[450px]">
                  <h3 className="tw-text-blue-2 tw-text-4xl sm:tw-text-5xl tw-font-semibold tw-mb-4">
                    30-Day Hire Promise
                  </h3>
                  <p className="tw-text-blue-2 tw-text-xl sm:tw-text-3xl lg:tw-text-2xl tw-font-semibold">
                    Speed meets quality. Our matching engine delivers results
                    fast — or we keep working until you are happy.
                  </p>
                </div>
                <div className="tw-bg-primary-blue-2 tw-rounded-xl tw-px-6 tw-py-4 tw-w-[300px] tw-flex-shrink-0 tw-flex tw-flex-col tw-justify-between tw-h-[450px]">
                  <h3 className="tw-text-blue-2 tw-text-4xl sm:tw-text-5xl tw-font-semibold tw-mb-4">
                    Real-Time Hiring Dashboard
                  </h3>
                  <p className="tw-text-blue-2 tw-text-xl sm:tw-text-3xl lg:tw-text-2xl tw-font-semibold">
                    Track candidate interest, application status, and team
                    performance. Full visibility, zero guesswork.
                  </p>
                </div>
                <div className="tw-bg-primary-blue-3 tw-rounded-xl tw-px-6 tw-py-4 tw-w-[300px] tw-flex-shrink-0 tw-flex tw-flex-col tw-justify-between tw-h-[450px]">
                  <h3 className="tw-text-blue-2 tw-text-4xl sm:tw-text-5xl tw-font-semibold tw-mb-4">
                    Diverse Talent Pipeline
                  </h3>
                  <p className="tw-text-blue-2 tw-text-xl sm:tw-text-3xl lg:tw-text-2xl tw-font-semibold">
                    Top university grads, skilled professionals, and career
                    switchers. Fresh perspectives, proven skills.
                  </p>
                </div>
                <div className="tw-bg-primary-blue-4 tw-rounded-xl tw-px-6 tw-py-4 tw-w-[300px] tw-flex-shrink-0 tw-flex tw-flex-col tw-justify-between tw-h-[450px]">
                  <h3 className="tw-text-blue-2 tw-text-4xl sm:tw-text-5xl tw-font-semibold tw-mb-4">
                    Custom Match Criteria
                  </h3>
                  <p className="tw-text-blue-2 tw-text-xl sm:tw-text-3xl lg:tw-text-2xl tw-font-semibold">
                    Filter by skills, experience, location, visa status—whatever
                    matters to your role.
                  </p>
                </div>
                <div className="tw-bg-pink tw-rounded-xl tw-px-6 tw-py-4 tw-w-[300px] tw-flex-shrink-0 tw-flex tw-flex-col tw-justify-between tw-h-[450px]">
                  <h3 className="tw-text-blue-2 tw-text-4xl sm:tw-text-5xl tw-font-semibold tw-mb-4">
                    Zero Hiring Fees
                  </h3>
                  <p className="tw-text-blue-2 tw-text-xl sm:tw-text-3xl lg:tw-text-2xl tw-font-semibold">
                    Post unlimited jobs. Hire unlimited talent. Pay absolutely
                    nothing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stats */}
      <section ref={statsSectionRef} className="tw-py-0 sm:tw-py-0">
        <div className="tw-w-full tw-max-w-[1682px] tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-14">
          <h2 className="tw-text-blue-3 tw-text-3xl sm:tw-text-4xl md:tw-text-5xl tw-font-semibold tw-text-center tw-mb-12 tw-bg-gradient-to-b from-blue-1 to-dark-blue-1 tw-bg-clip-text tw-text-transparent">
            Success Stats
          </h2>

          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-6">
            {successStats.map((stat, index) => (
              <div
                key={index}
                className={`${stat.bgColor} tw-rounded-3xl tw-p-6 sm:tw-p-8 tw-shadow-lg tw-border tw-border-gray-300 tw-relative tw-overflow-hidden`}
              >
                <div className="tw-relative tw-z-10 tw-text-left">
                  <h3 className="tw-text-blue-2 tw-text-3xl sm:tw-text-4xl md:tw-text-5xl lg:tw-text-6xl xl:tw-text-7xl tw-font-semibold tw-mb-4 tw-tabular-nums">
                    {statsVisible && (
                      <CountUp value={stat.value} duration={2000} />
                    )}
                  </h3>
                  <p className="tw-text-blue-2 tw-text-lg sm:tw-text-lg md:tw-text-xl tw-font-semibold">
                    {stat.label}
                  </p>
                </div>
                {stat.icon && (
                  <img
                    src={stat.icon}
                    alt=""
                    className="tw-absolute -tw-bottom-0 -tw-right-0 tw-w-24 tw-h-24 sm:tw-w-32 sm:tw-h-32 tw-opacity-80"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Board Section */}
      <section className="tw-py-16 sm:tw-py-20">
        <div className="tw-w-full">
          <h2 className="tw-text-blue-3 tw-text-4xl sm:tw-text-5xl md:tw-text-6xl tw-font-semibold tw-text-center  tw-bg-gradient-to-b from-blue-1 to-dark-blue-1 tw-bg-clip-text tw-text-transparent">
            Join The Largest Job Board
          </h2>
          <JobSlider />
        </div>
      </section>

      {/* Perfect For Section */}
      <section className="tw-py-8 sm:tw-py-10">
        <div className="tw-w-full tw-max-w-[1682px] tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-14">
          <h2 className="tw-text-blue-3 tw-text-4xl sm:tw-text-5xl md:tw-text-6xl tw-font-semibold tw-text-center tw-mb-12 tw-bg-gradient-to-b from-blue-1 to-dark-blue-1 tw-bg-clip-text tw-text-transparent">
            Perfect For
          </h2>

          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-6 tw-mb-12">
            {perfectForCards.map((card, index) => (
              <div
                key={index}
                className="tw-relative tw-bg-gray-2 tw-rounded-xl tw-overflow-hidden tw-shadow-lg tw-transition-all tw-duration-300 hover:tw-scale-105 tw-cursor-pointer hover:tw-shadow-2xl hover:tw-shadow-blue-500/100"
              >
                <img
                  src={card.image}
                  alt={card.title}
                  className="tw-w-full tw-h-[400px] sm:tw-h-[500px] lg:tw-h-[600px] tw-object-cover tw-rounded-xl"
                />
                <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-p-6">
                  <h3 className="tw-text-white tw-text-xl sm:tw-text-3xl lg:tw-text-3xl tw-font-semibold">
                    {card.title}
                  </h3>
                </div>
              </div>
            ))}
          </div>

          <div className="tw-text-center">
            <a href="#ContactUs">
              <button
                onClick={() => { }}
                className="tw-border-none tw-inline-block tw-bg-gradient-to-r tw-from-[#0843FE] tw-to-[#4995FB] tw-text-white tw-font-medium tw-py-3 tw-px-8 tw-rounded-md tw-transition tw-duration-300 tw-ease-in-out"
              >
                Get Started Today
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* Advantage Section */}
      <section className="tw-py-8 sm:tw-py-10">
        <div className="tw-w-full tw-max-w-[1682px] tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-14">
          <h2 className="tw-text-blue-3 tw-text-4xl sm:tw-text-5xl md:tw-text-6xl lg:tw-text-7xl xl:tw-text-8xl tw-font-semibold tw-text-center tw-mb-12">
            The SeekJobs Advantage
          </h2>

          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-12 lg:tw-gap-16 tw-max-w-5xl tw-mx-auto">
            <div className="tw-bg-gray-1 tw-rounded-3xl tw-p-8 tw-shadow-lg">
              <h3 className="tw-text-gray-1 tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl tw-font-semibold tw-text-center tw-mb-6">
                Traditional Hiring
              </h3>
              <div className="tw-relative tw-h-48 tw-overflow-hidden">
                <div className="tw-absolute tw-inset-x-0 tw-animate-scroll-up">
                  <div className="tw-text-center tw-space-y-4">
                    <p className="tw-text-gray-1 tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl tw-font-semibold">
                      60+ days
                    </p>
                    <p className="tw-text-gray-1 tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl tw-font-semibold">
                      Endless Resumes
                    </p>
                    <p className="tw-text-gray-1 tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl tw-font-semibold">
                      Hit-or-Miss Results
                    </p>
                    <p className="tw-text-gray-1 tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl tw-font-semibold">
                      And So on...
                    </p>
                  </div>
                  {/* Duplicate content for seamless loop */}
                  <div className="tw-text-center tw-space-y-4 tw-pt-48">
                    <p className="tw-text-gray-1 tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl tw-font-semibold">
                      60+ days
                    </p>
                    <p className="tw-text-gray-1 tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl tw-font-semibold">
                      Endless Resumes
                    </p>
                    <p className="tw-text-gray-1 tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl tw-font-semibold">
                      Hit-or-Miss Results
                    </p>
                    <p className="tw-text-gray-1 tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl tw-font-semibold">
                      And So on...
                    </p>
                  </div>
                </div>
                <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-b from-gray-1 via-transparent to-gray-1"></div>
              </div>
            </div>

            <div className="tw-rounded-3xl tw-p-8 tw-shadow-lg tw-shine-effect tw-bg-[#3176CE]">
              <div className="tw-relative tw-z-10">
                <h3 className="tw-text-white tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl tw-font-semibold tw-text-center tw-mb-6">
                  SeekJobs Hiring
                </h3>
                <div className="tw-text-center tw-space-y-4">
                  <p className="tw-text-white tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl tw-font-semibold">
                    30 days
                  </p>
                  <p className="tw-text-white tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl tw-font-semibold">
                    Curated Candidates
                  </p>
                  <p className="tw-text-white tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl tw-font-semibold">
                    Guaranteed Quality
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Start Options */}
      <section className="tw-py-16 sm:tw-py-20 tw-bg-gradient-to-b tw-from-[#081933] tw-to-[#184b99] tw-relative">
        <div
          className="tw-absolute tw-inset-0 tw-bg-cover tw-bg-center tw-opacity-20"
          style={{ backgroundImage: "url('/images/img_image_8.png')" }}
        ></div>
        <div className="tw-relative tw-w-full tw-max-w-[1682px] tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-14">
          <h2 className="tw-text-white tw-text-4xl sm:tw-text-5xl md:tw-text-6xl lg:tw-text-7xl xl:tw-text-8xl tw-font-semibold tw-text-center tw-mb-16">
            Quick Start Options
          </h2>
        </div>
      </section>

      {/* This grid is pulled up to overlap the section above.
          - `relative z-20` creates a new stacking context, ensuring these cards appear on top of the previous section.
          - Width, max-width, margin, and padding classes are added to align this grid with the page content.
      */}
      <div className="tw-relative tw-z-20 tw-w-full tw-max-w-[1682px] tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-14 tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-12 lg:tw-gap-16 -tw-mt-24">
        {quickStartOptions.map((option, index) => (
          <a href="#ContactUs">
            <div
              key={index}
              className="tw-bg-gray-1 tw-rounded-3xl tw-overflow-hidden tw-shadow-lg tw-group tw-transition-all tw-duration-300 tw-ease-in-out hover:tw-scale-105 hover:tw-shadow-2xl tw-cursor-pointer"
            >
              <img
                src={option.image}
                alt={option.title}
                className="tw-w-full tw-h-64 tw-object-cover tw-rounded-t-lg tw-transition-transform tw-duration-300 group-hover:tw-scale-110"
              />
              <div className="tw-p-6">
                <div className="tw-flex tw-justify-between tw-items-start">
                  <div className="tw-flex-1">
                    <h3 className="tw-text-dark-blue-1 tw-text-2xl sm:tw-text-3xl lg:tw-text-3xl tw-font-semibold tw-mb-2">
                      {option.title}
                    </h3>
                    <p className="tw-text-blue-1 tw-text-lg sm:tw-text-xl lg:tw-text-2xl tw-font-semibold">
                      {option.subtitle}
                    </p>
                  </div>
                  <img
                    src="/images/img_open.svg"
                    alt="Open"
                    className="tw-w-11 tw-h-11 tw-transition-transform tw-duration-300 group-hover:tw-scale-110"
                  />
                </div>
              </div>
            </div>
          </a>
        ))}
      </div>

      {/* CTA Section */}
      <section id="ContactUs" className="tw-py-16 sm:tw-py-20">
        <div className="tw-w-full tw-max-w-[1682px] tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-14">
          <h2 className="tw-text-blue-3 tw-text-3xl sm:tw-text-4xl md:tw-text-5xl lg:tw-text-6xl xl:tw-text-7xl tw-font-semibold tw-text-center tw-mb-16">
            Ready to revolutionize your hiring?
          </h2>

          <div className="tw-max-w-4xl tw-mx-auto">
            <div className="tw-bg-gradient-to-b tw-from-[#ccf2ff] tw-to-[#aacad4] tw-rounded-3xl tw-p-8 tw-shadow-lg tw-border tw-border-gray-300">
              <h3 className="tw-text-black tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-semibold tw-mb-8">
                Let's Connect
              </h3>

              <form onSubmit={handleSubmit} className="tw-space-y-6">
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                  <div>
                    <label className="tw-block tw-text-dark-2 tw-text-sm tw-font-bold tw-mb-1">
                      Full Name *
                    </label>
                    <EditText
                      placeholder="John Doe"
                      value={formData?.fullName}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow only letters and spaces
                        const filteredValue = value.replace(/[^a-zA-Z\s]/g, "");
                        setFormData((prev) => ({
                          ...prev,
                          fullName: filteredValue,
                        }));
                      }}

                      className={`tw-bg-transparent tw-border-global-6 ${errors.fullName ? "tw-border-red-500" : ""
                        }`}
                    />
                    {errors.fullName && (
                      <span className="tw-text-red-500 tw-text-xs">
                        {errors.fullName}
                      </span>
                    )}
                  </div>
                  <div>
                    <label className="tw-block tw-text-dark-2 tw-text-sm tw-font-bold tw-mb-1">
                      Company Name *
                    </label>
                    <EditText
                      placeholder="Example Inc"
                      value={formData?.companyName}
                      onChange={
                        (e) => {
                          const filteredValue = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                          setFormData((prev) => ({
                            ...prev,
                            companyName: filteredValue,
                          }))
                        }
                        // handleInputChange("companyName", e.target.value)
                      }
                      className={`tw-bg-transparent tw-border-global-6 ${errors.companyName ? "tw-border-red-500" : ""
                        }`}
                    />
                    {errors.companyName && (
                      <span className="tw-text-red-500 tw-text-xs">
                        {errors.companyName}
                      </span>
                    )}
                  </div>
                </div>

                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                  <div>
                    <label className="tw-block tw-text-dark-2 tw-text-sm tw-font-bold tw-mb-1">
                      Phone Number *
                    </label>
                    <EditText
                      placeholder="(123) 456-7890"
                      value={formData.phoneNumber}
                      onChange={handleInputChange("phoneNumber")}
                      onKeyDown={handleKeyDown}
                      className="tw-bg-global-8"
                    />
                    {errors.phoneNumber && (
                      <span className="tw-text-red-500 tw-text-xs">
                        {errors.phoneNumber}
                      </span>
                    )}
                  </div>
                  <div>
                    <label className="tw-block tw-text-dark-2 tw-text-sm tw-font-bold tw-mb-1">
                      Work Email Address *
                    </label>
                    <EditText
                      placeholder="johndoe@example.org"
                      type="email"
                      value={formData?.email}
                      onChange={(e) =>
                        // handleInputChange("email", e.target.value)
                        setFormData((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      className={`tw-bg-transparent tw-border-global-6 ${errors.email ? "tw-border-red-500" : ""
                        }`}
                    />
                    {errors.email && (
                      <span className="tw-text-red-500 tw-text-xs">
                        {errors.email}
                      </span>
                    )}
                  </div>
                </div>

                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                  <div>
                    <label className="tw-block tw-text-dark-2 tw-text-sm tw-font-bold tw-mb-3">
                      Team Size *
                    </label>
                    <div className="tw-flex tw-flex-wrap tw-gap-4">
                      {["1-10", "11-50", "51-200", "200+"].map((size) => (
                        <label
                          key={size}
                          className="tw-flex tw-items-center tw-gap-2"
                        >
                          <input
                            type="radio"
                            name="teamSize"
                            value={size}
                            checked={formData.teamSize === size}
                            onChange={handleInputChange("teamSize")}
                            className="tw-w-4 tw-h-4 tw-border tw-border-muted tw-rounded-lg"
                          />

                          <span className="tw-text-dark-2 tw-text-sm tw-font-semibold">
                            {size}
                          </span>
                        </label>
                      ))}
                      {errors.teamSize && (
                        <span className="tw-text-red-500 tw-text-xs">
                          {errors.teamSize}
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="tw-block tw-text-dark-2 tw-text-sm tw-font-semibold tw-mb-3">
                      Requirement *
                    </label>
                    <div className="tw-flex tw-flex-wrap tw-gap-4">
                      {["Immediate", "Planning", "Exploring"].map((req) => (
                        <label
                          key={req}
                          className="tw-flex tw-items-center tw-gap-2"
                        >
                          <input
                            type="radio"
                            name="requirement"
                            value={req}
                            checked={formData.requirement === req}
                            onChange={handleInputChange("requirement")}
                            className="tw-w-4 tw-h-4 tw-border tw-border-muted tw-rounded-lg"
                          />
                          <span className="tw-text-dark-2 tw-text-sm tw-font-semibold">
                            {req}
                          </span>
                        </label>
                      ))}
                      {errors.requirement && (
                        <span className="tw-text-red-500 tw-text-xs">
                          {errors.requirement}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="tw-block tw-text-dark-2 tw-text-sm tw-font-bold tw-mb-1">
                    Message *
                  </label>
                  <TextArea
                    placeholder="Enter Your Message"
                    value={formData.message}
                    onChange={handleInputChange("message")}
                    rows={6}
                    className="tw-bg-white-1"
                  />
                  {errors.message && (
                    <span className="tw-text-red-500 tw-text-xs">
                      {errors.message}
                    </span>
                  )}
                </div>

                <Button
                  type="submit"
                  variant="gradient"
                  size="large"
                  className={`tw-w-full tw-bg-[#0843FE] tw-text-white tw-p-[13px] tw-rounded `}
                  style={{ border: "none" }}
                >
                  <img
                    src="/images/img_vector_white_a700_12x12.svg"
                    alt="Call"
                    className="tw-w-5 tw-h-5 tw-mx-2"
                  />
                  Request a Call
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
      {statusMessage.show && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-6 tw-max-w-md tw-w-full tw-text-center">
            <h2
              className={`tw-text-lg tw-font-semibold ${statusMessage.type === 'success'
                ? 'tw-text-green-600'
                : 'tw-text-red-600'
                }`}
            >
              {statusMessage.type === 'success' ? 'Success' : 'Error'}
            </h2>
            <p className="tw-mt-2 tw-text-sm tw-text-gray-700">{statusMessage.text}</p>
            <button
              className="tw-mt-4 tw-px-4 tw-py-2 tw-bg-[#0843FE] tw-border-none tw-text-white tw-rounded hover:tw-bg-blue-700"
              onClick={() => setStatusMessage({ show: false, type: '', text: '' })}
            >
              Close
            </button>
          </div>
        </div>
      )}
      {/* Footer */}
      <CareerFooter theme="light" />
    </div>
  );
};

export default EmployerPage;